import { useRef, useState } from "react";
import { Menu, MenuItem, type Key } from "react-aria-components";
import { Infinity as InfinityIcon } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { MarketSignal } from "@/types/marketSignal.type";
import { KitSplitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import {
  DEFAULT_WINDOW_WEEKS,
  acceptWindow,
  formatShortDate,
  isEvergreenKind,
  validUntilFromWeeks,
  weeksLabel,
} from "./signalMeta";

/** What an accept sends, and whether it then graduates straight into Brand DNA. */
export interface AcceptChoice {
  validUntil: string | null;
  permanent: boolean;
}

export interface WindowOption {
  key: string;
  label: string;
  note: string;
  validUntil: string | null;
  suggested?: boolean;
  disabled?: boolean;
  permanent?: boolean;
}

const MENU_WEEKS = [DEFAULT_WINDOW_WEEKS, 12, 52];

/**
 * How long an accepted signal can steer (Signals / Window menu, 28 Sep): the
 * suggested window first, then 4, 12 or 52 weeks, Until superseded where the
 * kind allows it, and Permanent, which accepts the signal and graduates it into
 * Brand DNA as a fact with no end date. The suggestion is acceptWindow's, so a
 * suggestion already in the past is never offered (the API rejects it).
 */
export function windowOptions(signal: MarketSignal, now: Date = new Date()): WindowOption[] {
  const win = acceptWindow(signal, now);
  const evergreen = isEvergreenKind(signal.kind);
  const until = (iso: string) => `Until ${formatShortDate(iso, now)}`;
  const options: WindowOption[] = [];

  if (win.validUntil === null) {
    options.push({
      key: "superseded",
      label: "Until superseded",
      note: "Until a newer signal replaces it",
      validUntil: null,
      suggested: true,
    });
  } else {
    options.push({
      key: "suggested",
      label: win.durationLabel,
      note: until(win.validUntil),
      validUntil: win.validUntil,
      suggested: true,
    });
  }

  for (const weeks of MENU_WEEKS) {
    if (win.validUntil !== null && win.weeks === weeks) continue;
    const validUntil = validUntilFromWeeks(weeks, now);
    options.push({ key: `w${weeks}`, label: weeksLabel(weeks), note: until(validUntil), validUntil });
  }

  if (win.validUntil !== null) {
    options.push({
      key: "superseded",
      label: "Until superseded",
      note: evergreen
        ? "Until a newer signal replaces it"
        : "Only for positioning, audience and identity signals",
      validUntil: null,
      disabled: !evergreen,
    });
  }

  options.push({
    key: "permanent",
    label: "Permanent",
    note: "Straight into Brand DNA as a fact, with no end date",
    validUntil: win.validUntil,
    permanent: true,
  });
  return options;
}

/** The radio, the optional infinity mark, and the two lines of a window row. */
export function WindowOptionBody({ option, on }: { option: WindowOption; on: boolean }) {
  return (
    <>
      <span className={cn("sig-winmenu__radio", on && "is-on")} aria-hidden="true" />
      {option.permanent ? <InfinityIcon className="sig-winmenu__inf" aria-hidden="true" /> : null}
      <span className="sig-winmenu__col">
        <span className="sig-winmenu__line">
          <span className="sig-winmenu__label">{option.label}</span>
          {option.suggested ? <span className="sig-winmenu__tag">Suggested</span> : null}
        </span>
        <span className="sig-winmenu__note">{option.note}</span>
      </span>
    </>
  );
}

/**
 * Accept on the desk: one split capsule. The left half accepts for the
 * suggested window in one click; the arrow opens the window menu, and picking a
 * row accepts for that window at once.
 */
export default function AcceptSplit({
  signal,
  isDisabled = false,
  onAccept,
}: {
  signal: MarketSignal;
  isDisabled?: boolean;
  onAccept: (choice: AcceptChoice) => void;
}) {
  const [open, setOpen] = useState(false);
  const chooseRef = useRef<HTMLButtonElement>(null);
  const options = windowOptions(signal);
  const suggested = options[0];

  const pick = (key: Key) => {
    const option = options.find((o) => o.key === key);
    if (!option) return;
    setOpen(false);
    onAccept({ validUntil: option.validUntil, permanent: !!option.permanent });
  };

  return (
    <>
      <KitSplitButton
        ref={chooseRef}
        className="sig-card__accept"
        chooseLabel="Choose how long it steers"
        chooseExpanded={open}
        isDisabled={isDisabled}
        onPress={() => onAccept({ validUntil: suggested.validUntil, permanent: false })}
        onChoose={() => setOpen((o) => !o)}
      >
        Accept
      </KitSplitButton>
      <KitPopover
        triggerRef={chooseRef}
        isOpen={open}
        onOpenChange={setOpen}
        placement="bottom start"
        offset={8}
        className="sig-winmenu"
      >
        <p className="sig-winmenu__head" aria-hidden="true">
          How long it steers
        </p>
        <Menu
          aria-label="How long it steers"
          autoFocus="first"
          disabledKeys={options.filter((o) => o.disabled).map((o) => o.key)}
          onAction={pick}
          className="sig-winmenu__list"
        >
          {options.map((o) => (
            <MenuItem
              key={o.key}
              id={o.key}
              textValue={o.label}
              className={cn(
                "sig-winmenu__item",
                o.suggested && "is-suggested",
                o.permanent && "is-permanent",
              )}
            >
              <WindowOptionBody option={o} on={!!o.suggested} />
            </MenuItem>
          ))}
        </Menu>
      </KitPopover>
    </>
  );
}
